// Capture imports: bring a map, scan or model from another flight onto the
// timeline. Mirrors backend/common/capture_imports.py FILE_RULES.

export const FILE_KINDS = {
  tif: 'orthomosaic', tiff: 'orthomosaic',
  las: 'point_cloud', laz: 'point_cloud', e57: 'point_cloud', pcd: 'point_cloud', ply: 'point_cloud',
  obj: 'model_3d', glb: 'model_3d', gltf: 'model_3d',
  spz: 'site_scan', splat: 'site_scan',
  jpg: 'stills', jpeg: 'stills', png: 'stills',
  mp4: 'video', mov: 'video',
  pdf: 'report',
};
export const KIND_WORD = {
  orthomosaic: 'Map', elevation: 'Elevation', point_cloud: 'Point cloud', model_3d: '3D model',
  site_scan: 'Site scan', stills: 'Photo', video: 'Video', report: 'Report',
};
export const ACCEPT = Object.keys(FILE_KINDS).map((e) => `.${e}`).join(',');
export const MAX_FILES = 20;
export const MAX_BYTES = 5 * 1024 ** 3;
export const MULTIPART_OVER = 100 * 1024 ** 2;

export function extOf(name) {
  const base = String(name || '').split('?')[0].split('#')[0].split('/').pop();
  const i = base.lastIndexOf('.');
  return i > 0 ? base.slice(i + 1).toLowerCase() : '';
}

export function nameOf(url) {
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
  } catch {
    return '';
  }
}

export function kindOf(name) {
  return FILE_KINDS[extOf(name)] || null;
}

// A JPG/PNG with the same stem as a GeoTIFF becomes that map's picture.
export function pairedPreviews(names) {
  const stem = (n) => String(n).split('/').pop().replace(/\.[^.]+$/, '').toLowerCase();
  const maps = new Set(names.filter((n) => ['tif', 'tiff'].includes(extOf(n))).map(stem));
  return new Set(names.filter((n) => kindOf(n) === 'stills' && maps.has(stem(n))));
}

export function fileLine(name, bytes, previews) {
  if (previews && previews.has(name)) return 'Map picture';
  const parts = [KIND_WORD[kindOf(name)] || 'Not supported'];
  if (bytes) parts.push(formatBytes(bytes));
  return parts.join(' · ');
}

export function formatBytes(n) {
  const b = Number(n) || 0;
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
  if (b >= 1024 ** 2) return `${Math.round(b / 1024 ** 2)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
}

export function parseLinks(text) {
  return String(text || '').split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
}

export function todayIso(now = new Date()) {
  const p = (x) => String(x).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

// Plain reason the form can't go yet, or ''.
export function importError({ date, files, links }, now = new Date()) {
  const all = (files || []).length + (links || []).length;
  if (!date) return 'Add the date it was captured.';
  if (date > todayIso(now)) return 'The capture date can’t be in the future.';
  if (!all) return 'Choose a file or paste a link.';
  if (all > MAX_FILES) return `Up to ${MAX_FILES} files at a time.`;
  for (const f of files || []) {
    if (!kindOf(f.name)) return `${f.name} isn’t a map, scan, model, photo, video or PDF.`;
    if (f.size > MAX_BYTES) return `${f.name} is over 5 GB.`;
    if (!f.size) return `${f.name} is empty.`;
  }
  for (const l of links || []) {
    if (!/^https:\/\//i.test(l)) return 'Links must start with https://';
    if (!kindOf(nameOf(l))) return `${nameOf(l) || l} isn’t a file type we can show. Link straight to the file.`;
  }
  return '';
}

export const FIELD_LABEL = { api_key: 'API key', token: 'Access token', client_id: 'Client ID', client_secret: 'Client secret' };

export function providerLine(p) {
  switch (p.status) {
    case 'connected': return p.account_label ? `Connected · ${p.account_label}` : 'Connected';
    case 'needs_reconnect': return 'Reconnect to keep importing';
    case 'ready': return 'Not connected';
    case 'not_switched_on': return 'Not switched on yet';
    default: return 'Coming soon';
  }
}

export function shortDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return '';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function captureLine(c) {
  return [shortDay(c.date), c.site].filter(Boolean).join(' · ');
}
